/**
 * Automatic offline preload.
 *
 * The band's data (rider, config, musicians, crew, stageplots, setlists,
 * checklists, itinerary, songbook, team, daysheet/pdf layouts — plus the shared
 * team/shows/venues/contacts/calendar) is fetched through the normal apiGet*
 * layer, which caches every successful GET into the IndexedDB offline cache
 * automatically (see src/api.ts realFetchJSON → cachePut). So "preloading" is
 * just firing those fetches in the background — the offline cache fills itself
 * and no manual button is needed anymore.
 *
 * `preloadShowBundle` then walks the upcoming shows and caches everything a
 * Day Sheet needs (roadmap, hotels, rooming, flights, contacts, distances,
 * guests, notes, riders, show checklists) so a musician can open any upcoming
 * show in airplane mode — first time, without ever having visited it online.
 *
 * Everything here is fire-and-forget: callers never await it, so it can never
 * slow down login or band load. Jobs run sequentially (as the old manual
 * preload page did) to avoid hammering the server, and each band is fetched at
 * most once per session.
 */
import {
  apiGetRider,
  apiGetConfig,
  apiGetMusicians,
  apiGetCrew,
  apiGetStagePlots,
  apiGetSetlists,
  apiGetChecklists,
  apiGetItinerary,
  apiGetTeam,
  apiGetShows,
  apiGetVenues,
  apiGetContacts,
  apiGetCalendar,
  apiGetSongs,
  apiGetShow,
  apiGetShowRoadmap,
  apiGetAccommodations,
  apiGetRooming,
  apiGetShowFlights,
  apiGetShowContacts,
  apiGetShowDistances,
  apiGetShowGuests,
  apiGetShowTechNotes,
  apiGetShowMiscNotes,
  apiGetShowCategorizedNotes,
  apiGetRiders,
  apiGetShowChecklists,
  apiGetDaysheetLayoutEffective,
  apiGetPdfLayoutEffective,
  todayMinDate,
  type ShowDTO,
} from '../api';

/** How many upcoming Day Sheets the automatic preload walks (kept light). */
export const AUTO_PRELOAD_UPCOMING_LIMIT = 15;

const doneBands = new Set<string>();
const doneShowBundles = new Set<string>();
let globalDone = false;

/** Upcoming shows, dated and sorted — the ones a travelling user will open. */
function upcomingShows(shows: ShowDTO[], limit: number): ShowDTO[] {
  const today = todayMinDate();
  return shows
    .filter((s) => s.id && s.date && String(s.date) >= today)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .slice(0, limit);
}

/**
 * Cache every sub-resource a Day Sheet / show page reads. Silent: a failed
 * resource (404 on a band without that data, offline at that moment) never
 * aborts the rest of the bundle.
 */
export async function preloadShowBundle(showId: string, bandId?: string): Promise<void> {
  if (!showId) return;
  const key = `${bandId ?? ''}:${showId}`;
  if (doneShowBundles.has(key)) return;
  doneShowBundles.add(key);
  const jobs: Array<() => Promise<unknown>> = [
    () => apiGetShow(showId),
    () => apiGetShowRoadmap(showId),
    () => apiGetAccommodations(showId),
    () => apiGetRooming(showId),
    () => apiGetShowFlights(showId),
    () => apiGetShowContacts(showId),
    () => apiGetShowDistances(showId),
    () => apiGetShowGuests(showId),
    () => apiGetShowTechNotes(showId),
    () => apiGetShowMiscNotes(showId),
    () => apiGetShowCategorizedNotes(showId),
    () => apiGetRiders({ showId }),
  ];
  if (bandId) jobs.push(() => apiGetShowChecklists(bandId, showId));
  await Promise.allSettled(jobs.map((run) => run()));
}

/** Cache the Day Sheets of the closest upcoming shows (sequentially). */
export async function preloadUpcomingShows(
  shows: ShowDTO[],
  limit: number = AUTO_PRELOAD_UPCOMING_LIMIT,
): Promise<void> {
  for (const show of upcomingShows(shows, limit)) {
    try {
      await preloadShowBundle(String(show.id), show.band_id ? String(show.band_id) : undefined);
    } catch {
      /* offline / failure — keep going with the next show */
    }
  }
}

/** Fetch the shared (non-band-scoped) data into the offline cache, once. */
async function preloadGlobal(): Promise<void> {
  if (globalDone) return;
  globalDone = true;
  let shows: ShowDTO[] = [];
  try { shows = await apiGetShows(); } catch { /* cache stays as-is */ }
  const jobs: Array<() => Promise<unknown>> = [
    () => apiGetTeam(),
    // Upcoming slice — the Route map / Atlas asks for min_date=YYYY-MM-DD.
    () => apiGetShows({ minDate: todayMinDate() }),
    () => apiGetVenues(),
    () => apiGetContacts(),
    () => apiGetCalendar(),
  ];
  for (const run of jobs) {
    try { await run(); } catch { /* offline / failure — cache stays as-is */ }
  }
  await preloadUpcomingShows(shows, AUTO_PRELOAD_UPCOMING_LIMIT);
}

/** Fetch one band's data into the offline cache, once per session. */
async function preloadBand(bandId: string): Promise<void> {
  if (!bandId || doneBands.has(bandId)) return;
  doneBands.add(bandId);
  const jobs = [
    apiGetRider, apiGetConfig, apiGetMusicians, apiGetCrew,
    apiGetStagePlots, apiGetSetlists, apiGetChecklists, apiGetItinerary,
    apiGetSongs,
  ];
  for (const fn of jobs) {
    try { await fn(bandId); } catch { /* offline / failure — cache stays as-is */ }
  }
  const extras: Array<() => Promise<unknown>> = [
    // People of THIS band are what the Day Sheet / rider pages list.
    () => apiGetTeam({ band: bandId }),
    () => apiGetDaysheetLayoutEffective(bandId),
    () => apiGetPdfLayoutEffective(bandId),
  ];
  for (const run of extras) {
    try { await run(); } catch { /* offline / failure — cache stays as-is */ }
  }
}

/** Fire-and-forget preload for one band (no-op if already done). */
export function autoPreloadBand(bandId: string): void {
  if (!bandId || doneBands.has(bandId)) return;
  // Do not await — non-blocking by design.
  void preloadBand(bandId);
}

/** Fire-and-forget preload for every band the user can access + shared data. */
export function autoPreloadAll(bandIds: string[]): void {
  if (!globalDone) void preloadGlobal();
  for (const id of bandIds) autoPreloadBand(id);
}
